import React, { useEffect, useState } from 'react';
import { Copy, Loader2, Save, TestTube2 } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import {
  CALLBACK_PATH, CASHMONEY_DEFAULTS, changedFields,
} from '../lib/cashmoney';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

/**
 * Ayarlar → Finans → CashMoney Pay.
 *
 * Bölüm kendi state'ini ve Kaydet düğmesini taşır: ayarlar
 * `provider_configs` satırındadır ve `PATCH /admin/settings` gövdesine hiç
 * girmez. `onEnabledChange` finans bölüm seçicisindeki aktiflik noktasını
 * besler.
 */
const CashMoneySettings = ({ paymentBase = '', onEnabledChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(CASHMONEY_DEFAULTS);
  const [initial, setInitial] = useState(CASHMONEY_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  // Ayarlar okunamadıysa form varsayılanları çizer; kaydetmek onları canlının
  // üzerine yazmak olurdu, bu yüzden bölüm okunana kadar salt okunurdur.
  const [loadError, setLoadError] = useState('');

  const callbackUrl = `${paymentBase || window.location.origin}${CALLBACK_PATH}`;

  const apply = (data) => {
    const merged = {
      ...CASHMONEY_DEFAULTS,
      ...data,
      api_secret: '',
      webhook_secret: '',
    };
    setForm(merged);
    setInitial(merged);
    setMeta(data || {});
    onEnabledChange?.(Boolean(data?.enabled));
  };

  useEffect(() => {
    let alive = true;
    client.get('/admin/cashmoney/config')
      .then(({ data }) => {
        if (!alive) return;
        apply(data);
        setLoadError('');
      })
      .catch((error) => {
        if (!alive) return;
        const message = errorText(error, 'Bilinmeyen hata');
        setLoadError(message);
        toast({
          title: 'CashMoney Pay ayarları okunamadı',
          description: message,
          variant: 'destructive',
        });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const pending = changedFields(
    form, initial, { paymentBase: paymentBase || window.location.origin });
  const dirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (loadError || !dirty) return;
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/cashmoney/config', pending);
      apply(data);
      toast({ title: 'CashMoney Pay ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'CashMoney Pay kaydedilemedi',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const { data } = await client.post('/admin/cashmoney/test');
      toast({ title: data?.message || 'CashMoney Pay ayarları geçerli' });
    } catch (error) {
      toast({
        title: 'CashMoney Pay testi başarısız',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast({ title: 'Kopyalandı' }))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> CashMoney Pay ayarları yükleniyor…
      </div>
    );
  }

  return (
    <>
      <div className="col-span-full mt-1">
        <h3 className="text-sm font-bold text-white">CashMoney Pay</h3>
        <p className="mt-1 text-xs text-slate-400">
          Havale/EFT yatırım ve çekim (H2H). Çekim önce
          <strong> yönetici onayından </strong> geçer, sonra sağlayıcıya
          iletilir; sonuç HMAC imzalı bildirimle kesinleşir. Ayarlar kaydedilince
          oyuncunun yöntem listesindeki <strong>CashMoney Pay Havale / EFT</strong>
          {' '}satırı da güncellenir.
        </p>
      </div>

      {loadError && (
        <div className="col-span-full rounded-lg border border-rose-500/40 p-3 text-xs text-rose-200">
          Ayarlar okunamadı ({loadError}); bölüm salt okunur.
        </div>
      )}

      <div className="col-span-full provider-enable">
        <label className="flex items-center gap-2 text-sm font-semibold text-white">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
          />
          CashMoney Pay entegrasyonu aktif
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Açmak için API anahtarı, API secret, webhook secret ve bildirim adresi
          onayı gerekir.
        </p>
      </div>

      <label className="settings-inline-field">
        API adresi
        <input
          value={form.api_url || ''}
          onChange={(e) => set('api_url', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        API anahtarı (X-API-Key)
        <input
          value={form.api_key || ''}
          onChange={(e) => set('api_key', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        API secret (X-API-Secret)
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.api_secret_configured ? 'Kayıtlı — değiştirmek için yazın' : 'API secret'}
          value={form.api_secret}
          onChange={(e) => set('api_secret', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        Webhook secret
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.webhook_secret_configured ? 'Kayıtlı — değiştirmek için yazın' : 'Webhook imza anahtarı'}
          value={form.webhook_secret}
          onChange={(e) => set('webhook_secret', e.target.value)}
          className="admin-input mt-1"
        />
      </label>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="text-xs text-slate-300">Bildirim adresi (yatırım ve çekim için aynı)</div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200">
            {callbackUrl}
          </code>
          <button type="button" onClick={() => copy(callbackUrl)} className="admin-btn-secondary p-2" aria-label="Kopyala">
            <Copy size={13} />
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Adres her istekte sağlayıcıya gönderilir. API adresi, anahtar, webhook
          secret ya da bu adres değiştiğinde onay kendiliğinden kalkar.
        </p>
        <label className="mt-2 flex items-center gap-2 text-xs text-white">
          <input
            type="checkbox"
            checked={!!form.callback_registered}
            onChange={(e) => set('callback_registered', e.target.checked)}
          />
          Bildirim adresini ve webhook secret'ı sağlayıcı panelinde tanımladım
        </label>
      </div>

      {[
        { key: 'deposit', title: 'yatırım', cls: 'is-deposit' },
        { key: 'withdraw', title: 'çekim', cls: 'is-withdraw' },
      ].map((side) => (
        <section key={side.key} className={`provider-money ${side.cls}`}>
          <label className="flex items-center gap-2 text-sm font-bold text-white">
            <input
              type="checkbox"
              checked={form[`${side.key}_enabled`] !== false}
              onChange={(e) => set(`${side.key}_enabled`, e.target.checked)}
            />
            CashMoney Pay {side.title} açık
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['min', 'max'].map((bound) => (
              <label key={bound} className="settings-inline-field">
                {bound === 'min' ? 'Minimum' : 'Maksimum'} {side.title} (₺)
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form[`${side.key}_${bound}_amount`] ?? ''}
                  onChange={(e) => set(`${side.key}_${bound}_amount`, Number(e.target.value))}
                  className="admin-input mt-1"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="col-span-full flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!!busy || !dirty || !!loadError}
          className="admin-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          CashMoney Pay Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={test}
          disabled={!!busy || !meta.configured}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Ayarları Doğrula
        </button>
      </div>
    </>
  );
};

export default CashMoneySettings;
